import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import logo from "../../../assets/logo.png";
import styles from "./RegisterPage.module.scss";
import useFetchBranches from "../../../hooks/useFetchBranches";
import useFetchCarBrands from "../../../hooks/useFetchCarBrands";
import useFetchCarColors from "../../../hooks/useFetchCarColors";
import useFetchCarModels from "../../../hooks/useFetchCarModels";
import BranchSelect from "../../forms/BranchSelect";
import CarBrandSelect from "../../forms/carForms/CarBrandSelect";
import CarColorSelect from "../../forms/carForms/CarColorSelect";
import CarModelSelect from "../../forms/carForms/CarModelSelect";
import SuccessModal from "../../modal/SuccessModal";
import PrivacyPolicy from "../Privacy/PrivacyPolicy";
import StarField from "../../StarField/StarField";
import CustomSelect from "../../forms/CustomSelect/CustomSelect";
import DateField from "../../forms/DateField/DateField";
import {
  RegisterFormValues,
  registerSchema,
  transforms,
} from "../../../schemas/registerSchema";

const CAR_CLASSES: Array<{ value: string; label: string }> = [
  { value: "1", label: "Эконом" },
  { value: "2", label: "Комфорт" },
  { value: "107", label: "Комфорт+" },
  { value: "28", label: "Автокурьер" },
  { value: "199", label: "Женский" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: CURRENT_YEAR - 1995 + 1 }, (_, i) =>
  String(CURRENT_YEAR - i)
);

// Границы для нативного date-пикера — те же 18–80 лет, что и в схеме.
const isoShift = (years: number) => {
  const d = new Date();
  d.setFullYear(d.getFullYear() - years);
  return d.toISOString().slice(0, 10);
};
const BIRTHDAY_MAX = isoShift(18);
const BIRTHDAY_MIN = isoShift(80);

const RegisterDriverPage: React.FC = () => {
  const [currentTime] = useState(() => Math.floor(Date.now() / 1000));
  const [showModal, setShowModal] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showAgreement, setShowAgreement] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const branchList = useFetchBranches(currentTime);
  const carBrandList = useFetchCarBrands(currentTime);
  const carColorList = useFetchCarColors(currentTime);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<RegisterFormValues>({
    resolver: yupResolver(registerSchema),
    mode: "onBlur",
    defaultValues: {
      last_name: "",
      name: "",
      second_name: "",
      phone: "",
      birthday: "",
      city_id: "",
      card_number: "",
      driver_card_name: "",
      driver_card_surname: "",
      car_brand: "",
      car_model: "",
      car_color: "",
      car_year: "",
      car_class: "",
      gos_number: "",
    },
  });

  const selectedCarBrand = watch("car_brand");
  const carModelList = useFetchCarModels(currentTime, selectedCarBrand);

  const onSubmit = async (values: RegisterFormValues) => {
    setSubmitError(null);
    try {
      const response = await fetch(`/api/create_worker_profile`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...values,
          activate: "1",
          car_owner: "WORKER",
          position_id: "1",
          partnership: "STAFF",
          // Считаем момент отправки, а не монтирования: вкладка может
          // провисеть открытой сколько угодно, подпись протухнет.
          current_time: Math.floor(Date.now() / 1000),
        }),
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(
          data?.error ?? "Не удалось создать анкету водителя. Попробуйте ещё раз."
        );
      }

      // Внешнее API отвечает 200 и кладёт результат в code/info:
      // code !== 0 — это отказ (например, водитель уже зарегистрирован).
      if (data && typeof data.code !== "undefined" && Number(data.code) !== 0) {
        throw new Error(
          data.info || "Не удалось создать анкету водителя. Проверьте данные."
        );
      }

      setShowModal(true);
    } catch (error) {
      console.error("Error creating driver profile:", error);
      setSubmitError(
        error instanceof Error ? error.message : "Произошла ошибка. Попробуйте ещё раз."
      );
    }
  };

  return (
    <div className={styles.wrapper}>
      <StarField />
      <div className={styles.container}>
        <div className={styles.logoCont}>
          <img src={logo} alt="" />
          <div className={styles.title}>SMART Taxi</div>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className={styles.form} noValidate>
          <div className={styles.driver}>
            <div className={styles.driverForm}>
              <p className={styles.driverTitle}>Личная информация</p>
              <div className={styles.driverInfo}>
                <div className={styles.lastName}>
                  <label className={styles.lastNameTitle} htmlFor="last_name">
                    Фамилия
                  </label>
                  <Controller
                    name="last_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="last_name"
                        type="text"
                        placeholder="Иванов"
                        autoComplete="family-name"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.ruName(e.target.value))
                        }
                      />
                    )}
                  />
                  {errors.last_name && (
                    <span className={styles.fieldError}>{errors.last_name.message}</span>
                  )}
                </div>
                <div className={styles.name}>
                  <label className={styles.nameTitle} htmlFor="name">
                    Имя
                  </label>
                  <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="name"
                        type="text"
                        placeholder="Иван"
                        autoComplete="given-name"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.ruName(e.target.value))
                        }
                      />
                    )}
                  />
                  {errors.name && (
                    <span className={styles.fieldError}>{errors.name.message}</span>
                  )}
                </div>
                <div className={styles.secondName}>
                  <label className={styles.secondNameTitle} htmlFor="second_name">
                    Отчество
                  </label>
                  <Controller
                    name="second_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="second_name"
                        type="text"
                        placeholder="Иванович"
                        autoComplete="additional-name"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.ruName(e.target.value))
                        }
                      />
                    )}
                  />
                  {errors.second_name && (
                    <span className={styles.fieldError}>{errors.second_name.message}</span>
                  )}
                </div>
                <div className={styles.phone}>
                  <label className={styles.phoneTitle} htmlFor="phone">
                    Телефон
                  </label>
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="phone"
                        type="tel"
                        placeholder="+79281234567"
                        autoComplete="tel"
                        inputMode="tel"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) => field.onChange(transforms.phone(e.target.value))}
                      />
                    )}
                  />
                  {errors.phone && (
                    <span className={styles.fieldError}>{errors.phone.message}</span>
                  )}
                </div>
                <div className={styles.birthday}>
                  <label className={styles.birthdayTitle} htmlFor="birthday">
                    Дата рождения
                  </label>
                  <Controller
                    name="birthday"
                    control={control}
                    render={({ field }) => (
                      <DateField
                        id="birthday"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        min={BIRTHDAY_MIN}
                        max={BIRTHDAY_MAX}
                      />
                    )}
                  />
                  {errors.birthday && (
                    <span className={styles.fieldError}>{errors.birthday.message}</span>
                  )}
                </div>
                <Controller
                  name="city_id"
                  control={control}
                  render={({ field }) => (
                    <BranchSelect
                      branches={branchList}
                      selectedBranch={field.value}
                      onChange={(v) => field.onChange(v)}
                    />
                  )}
                />
                {errors.city_id && (
                  <span className={styles.fieldError}>{errors.city_id.message}</span>
                )}
              </div>
            </div>
            <div className={styles.cardForm}>
              <p className={styles.cardTitle}>Банковская карта</p>
              <p className={styles.cardText}>
                Обратите внимание, что эта карта будет использоваться для вывода
                средств, поэтому убедитесь, что информация введена корректно.
              </p>
              <div className={styles.cardInfo}>
                <div className={styles.cardNumber}>
                  <label className={styles.cardNumberTitle} htmlFor="card_number">
                    Номер банковской карты
                  </label>
                  <Controller
                    name="card_number"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="card_number"
                        type="text"
                        inputMode="numeric"
                        autoComplete="cc-number"
                        placeholder="XXXX XXXX XXXX XXXX"
                        maxLength={19}
                        value={transforms.cardGroups(field.value)}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.digits(e.target.value).slice(0, 16))
                        }
                      />
                    )}
                  />
                  {errors.card_number && (
                    <span className={styles.fieldError}>{errors.card_number.message}</span>
                  )}
                </div>
                <div className={styles.cardName}>
                  <label className={styles.cardNameTitle} htmlFor="driver_card_name">
                    Имя как на карте
                  </label>
                  <Controller
                    name="driver_card_name"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="driver_card_name"
                        type="text"
                        placeholder="IVAN"
                        autoComplete="cc-given-name"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.latinUpper(e.target.value))
                        }
                      />
                    )}
                  />
                  {errors.driver_card_name && (
                    <span className={styles.fieldError}>
                      {errors.driver_card_name.message}
                    </span>
                  )}
                </div>
                <div className={styles.cardLastname}>
                  <label
                    className={styles.cardLastnameTitle}
                    htmlFor="driver_card_surname"
                  >
                    Фамилия как на карте
                  </label>
                  <Controller
                    name="driver_card_surname"
                    control={control}
                    render={({ field }) => (
                      <input
                        id="driver_card_surname"
                        type="text"
                        placeholder="IVANOV"
                        autoComplete="cc-family-name"
                        value={field.value}
                        onBlur={field.onBlur}
                        onChange={(e) =>
                          field.onChange(transforms.latinUpper(e.target.value))
                        }
                      />
                    )}
                  />
                  {errors.driver_card_surname && (
                    <span className={styles.fieldError}>
                      {errors.driver_card_surname.message}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className={styles.carForm}>
              <div className={styles.car}>
                <p className={styles.carInfoTitle}>Автомобиль</p>
                <div className={styles.carInfo}>
                  <div>
                    <Controller
                      name="car_brand"
                      control={control}
                      render={({ field }) => (
                        <CarBrandSelect
                          carBrands={carBrandList}
                          selectedCarBrand={field.value}
                          onChange={(v) => {
                            field.onChange(v);
                            setValue("car_model", "");
                          }}
                        />
                      )}
                    />
                    {errors.car_brand && (
                      <span className={styles.fieldError}>{errors.car_brand.message}</span>
                    )}
                  </div>
                  <div>
                    <Controller
                      name="car_model"
                      control={control}
                      render={({ field }) => (
                        <CarModelSelect
                          carModels={carModelList}
                          selectedCarModel={field.value}
                          onChange={(v) => field.onChange(v)}
                          disabled={!selectedCarBrand}
                        />
                      )}
                    />
                    {errors.car_model && (
                      <span className={styles.fieldError}>{errors.car_model.message}</span>
                    )}
                  </div>
                  <div>
                    <Controller
                      name="car_color"
                      control={control}
                      render={({ field }) => (
                        <CarColorSelect
                          carColors={carColorList}
                          selectedCarColor={field.value}
                          onChange={(v) => field.onChange(v)}
                        />
                      )}
                    />
                    {errors.car_color && (
                      <span className={styles.fieldError}>{errors.car_color.message}</span>
                    )}
                  </div>
                  <div>
                    <Controller
                      name="car_year"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          id="car_year"
                          label="Год выпуска авто"
                          placeholder="Выберите год"
                          options={YEARS.map((y) => ({ value: y, label: y }))}
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                        />
                      )}
                    />
                    {errors.car_year && (
                      <span className={styles.fieldError}>{errors.car_year.message}</span>
                    )}
                  </div>
                  <div>
                    <Controller
                      name="car_class"
                      control={control}
                      render={({ field }) => (
                        <CustomSelect
                          id="car_class"
                          label="Класс авто"
                          placeholder="Выберите класс"
                          options={CAR_CLASSES}
                          value={field.value}
                          onChange={(v) => field.onChange(v)}
                        />
                      )}
                    />
                    {errors.car_class && (
                      <span className={styles.fieldError}>{errors.car_class.message}</span>
                    )}
                  </div>
                  <div>
                    <label className={styles.gosNomerTitle} htmlFor="gos_number">
                      Гос номер
                    </label>
                    <Controller
                      name="gos_number"
                      control={control}
                      render={({ field }) => (
                        <input
                          id="gos_number"
                          type="text"
                          placeholder="а001аа95"
                          maxLength={9}
                          value={field.value}
                          onBlur={field.onBlur}
                          onChange={(e) =>
                            field.onChange(transforms.ruAlnumLower(e.target.value))
                          }
                        />
                      )}
                    />
                    {errors.gos_number && (
                      <span className={styles.fieldError}>{errors.gos_number.message}</span>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className={styles.agreement}>
            <input
              type="checkbox"
              id="agree"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
            />
            <label htmlFor="agree">Я согласен на обработку персональных данных</label>
            <button
              type="button"
              className={styles.policy}
              onClick={() => setShowAgreement(true)}
            >
              Политика конфиденциальности
            </button>
          </div>
          {submitError && (
            <div className={styles.submitError} role="alert">
              {submitError}
            </div>
          )}
          <button type="submit" disabled={!isAgreed || isSubmitting}>
            {isSubmitting ? "Отправка..." : "Создать аккаунт"}
          </button>
        </form>
        {showAgreement && <PrivacyPolicy onClose={() => setShowAgreement(false)} />}
        {showModal && <SuccessModal onClose={() => setShowModal(false)} />}
      </div>
    </div>
  );
};

export default RegisterDriverPage;
